import React from 'react';
/** import third-party libraries in the section above, Sort Lines Ascending */

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
/** import mui components in the section above, Sort Lines Ascending */

import { sortRecordsByName } from 'states/resources/organizations/business-logic';
import NotFoundRecipientLabel from './not-found-recipient-label';
/** import classes and others in the section above, Sort Lines Ascending */

/**
 * Checkbox list for picking schedule report recipients.
 * Header fixed; list body scrolls (max-height 400).
 * Active users from API (deprecated/suspended filtered); missing selected emails render as not-found stubs.
 *
 * @param {Object} props
 * @param {Array<{id: *, name: string, email: string}>} props.users - Active org users from API
 * @param {string[]} props.selectedEmails - Selected emails; missing users shown as not-found stubs
 * @param {function(React.ChangeEvent<HTMLInputElement>): void} props.onToggle
 *   Parent toggle handler; expects MUI checkbox event shape (reads e.target.value)
 * @returns {React.ReactElement}
 */
function RecipientsSelector(props) {
    const { users, selectedEmails, onToggle } = props;
    const activeUsers = users || [];
    const usersByEmail = Object.fromEntries(activeUsers.map(user => [user.email, user]));
    // Stubs for selected emails not in the current org's active user list
    const stubs = (selectedEmails || [])
        .filter(email => !usersByEmail[email])
        .map(email => ({ id: null, name: null, email }));
    const sortedUsers = sortRecordsByName([...activeUsers, ...stubs], { nameField: 'name' });

    /**
     * Toggles a recipient by email, adapting to the parent onToggle contract.
     * @param {string} email
     */
    const handleToggle = email => {
        // Synthetic event — parent handleScheduleToggleRecipient reads e.target.value
        onToggle({ target: { value: email } });
    };

    return (
        <Box className="reports-recipients-selector">
            <Box className="reports-recipients-selector__header">
                <Box className="reports-recipients-selector__checkbox-spacer" aria-hidden="true" />
                <Typography variant="body2" className="reports-recipients-selector__name-column">
                    Name
                </Typography>
                <Typography variant="body2" className="reports-recipients-selector__email-column">
                    Email
                </Typography>
            </Box>
            {/* Rows in scrollable list; header stays fixed outside scroll area */}
            <Box className="reports-recipients-selector__list">
                {sortedUsers.map(user => (
                    <Box
                        key={user.id ?? user.email}
                        className="reports-recipients-selector__row"
                        role="button"
                        tabIndex={0}
                        onClick={() => handleToggle(user.email)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleToggle(user.email);
                            }
                        }}
                    >
                        <Checkbox
                            className="reports-recipients-selector__checkbox"
                            checked={(selectedEmails || []).includes(user.email)}
                            // Prevent row onClick from firing when checkbox is clicked directly (avoids double-toggle)
                            onClick={e => e.stopPropagation()}
                            onChange={() => handleToggle(user.email)}
                            value={user.email}
                            inputProps={{ 'aria-label': `Toggle recipient ${user.email}` }}
                            color="primary"
                        />
                        <Typography
                            variant="body2"
                            className="reports-recipients-selector__name-column"
                        >
                            {user.id === null ? <NotFoundRecipientLabel /> : user.name}
                        </Typography>
                        <Typography
                            variant="body2"
                            className="reports-recipients-selector__email-column"
                        >
                            {user.email}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

export default RecipientsSelector;
